const PLACEMENTS = ["right", "left", "bottom", "top"];

export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const toPlainRect = (domRect) => {
  if (!domRect) return null;
  const left = domRect.left ?? 0;
  const top = domRect.top ?? 0;
  const width = domRect.width ?? 0;
  const height = domRect.height ?? 0;
  return {
    left,
    top,
    width,
    height,
    right: left + width,
    bottom: top + height,
  };
};

export const expandRect = (rect, paddingPx = 0) => {
  if (!rect) return null;
  const pad = Number(paddingPx) || 0;
  const left = rect.left - pad;
  const top = rect.top - pad;
  const width = rect.width + pad * 2;
  const height = rect.height + pad * 2;
  return {
    left,
    top,
    width,
    height,
    right: left + width,
    bottom: top + height,
  };
};

const getViewportSize = () => ({
  width: typeof window !== "undefined" ? window.innerWidth : 0,
  height: typeof window !== "undefined" ? window.innerHeight : 0,
});

const computeUnclamped = (placement, targetRect, coachmarkSize, gapPx) => {
  const gap = Number(gapPx) || 0;
  const w = coachmarkSize?.width ?? 0;
  const h = coachmarkSize?.height ?? 0;

  switch (placement) {
    case "right":
      return {
        left: targetRect.right + gap,
        top: targetRect.top + (targetRect.height - h) / 2,
      };
    case "left":
      return {
        left: targetRect.left - gap - w,
        top: targetRect.top + (targetRect.height - h) / 2,
      };
    case "bottom":
      return {
        left: targetRect.left + (targetRect.width - w) / 2,
        top: targetRect.bottom + gap,
      };
    case "top":
      return {
        left: targetRect.left + (targetRect.width - w) / 2,
        top: targetRect.top - gap - h,
      };
    default:
      return {
        left: targetRect.right + gap,
        top: targetRect.top + (targetRect.height - h) / 2,
      };
  }
};

const clampToViewport = (pos, coachmarkSize, viewportPaddingPx, viewportSize) => {
  const pad = Number(viewportPaddingPx) || 0;
  const w = coachmarkSize?.width ?? 0;
  const h = coachmarkSize?.height ?? 0;
  const maxLeft = Math.max(pad, (viewportSize?.width ?? 0) - w - pad);
  const maxTop = Math.max(pad, (viewportSize?.height ?? 0) - h - pad);

  const left = clamp(pos.left, pad, maxLeft);
  const top = clamp(pos.top, pad, maxTop);

  // Penalty is the adjustment distance required to fit.
  const penalty = Math.abs(left - pos.left) + Math.abs(top - pos.top);

  return { left, top, penalty };
};

/**
 * Compute a fixed-position coachmark placement near a highlighted target element.
 *
 * @param {Object} args
 * @param {Object|null} args.targetRect Plain rect {left,top,right,bottom,width,height}
 * @param {Object} args.coachmarkSize {width,height}
 * @param {'auto'|'right'|'left'|'top'|'bottom'} args.placement Preferred placement
 * @param {number} args.gapPx Gap between target and coachmark
 * @param {number} args.viewportPaddingPx Padding from viewport edges
 * @returns {{left:number, top:number, placement:string}}
 */
export const computeCoachmarkPosition = ({
  targetRect,
  coachmarkSize,
  placement = "auto",
  gapPx = 12,
  viewportPaddingPx = 12,
}) => {
  const viewport = getViewportSize();
  const w = coachmarkSize?.width ?? 0;
  const h = coachmarkSize?.height ?? 0;

  // Fallback: center in viewport when no target
  if (!targetRect) {
    const left = clamp((viewport.width - w) / 2, viewportPaddingPx, Math.max(viewportPaddingPx, viewport.width - w - viewportPaddingPx));
    const top = clamp((viewport.height - h) / 2, viewportPaddingPx, Math.max(viewportPaddingPx, viewport.height - h - viewportPaddingPx));
    return { left, top, placement: "center" };
  }

  const candidates =
    placement && placement !== "auto" && PLACEMENTS.includes(placement)
      ? [placement]
      : ["right", "left", "bottom", "top"];

  let best = null;

  candidates.forEach((p) => {
    const raw = computeUnclamped(p, targetRect, coachmarkSize, gapPx);
    const clamped = clampToViewport(raw, coachmarkSize, viewportPaddingPx, viewport);
    const candidate = { left: clamped.left, top: clamped.top, placement: p, penalty: clamped.penalty };
    if (!best || candidate.penalty < best.penalty) best = candidate;
  });

  if (!best) {
    return { left: viewportPaddingPx, top: viewportPaddingPx, placement: "right" };
  }

  return { left: best.left, top: best.top, placement: best.placement };
};

